import React, { useState, useEffect } from 'react';
import { Crown, Timer, AlertCircle, Calendar, Clock, CheckCircle, User } from 'lucide-react';
import './UserInfoTabContent.css';

const UserInfoTabContent = ({ 
  persistentUserId,
  currentVoice,
  isIdVisible,
  setIsIdVisible,
  copied,
  setCopied,
  firestoreStorage,
  isStorageInitialized,
  changeVoice,
  handleStartOver,
  handleSignOut,
  setShowSummary,
  timeRemaining, // Add this prop
  isRecording,   // Add this prop to know if currently in a call
  callEnded      // Add this prop to know if call has ended
}) => {
  const [trialStatus, setTrialStatus] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [callTimer, setCallTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [lastKnownTimeRemaining, setLastKnownTimeRemaining] = useState(null);

  // Track the last known time remaining to persist after call ends
  useEffect(() => {
    if (timeRemaining !== undefined && timeRemaining !== null) {
      setLastKnownTimeRemaining(timeRemaining);
    }
  }, [timeRemaining]);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('UserInfoTabContent state update:', {
      isRecording,
      callEnded,
      timeRemaining,
      lastKnownTimeRemaining,
      'callTimer.remainingTime': callTimer?.remainingTime,
      'trialStatus.minutesLeft': trialStatus?.minutesLeft
    });
  }, [isRecording, callEnded, timeRemaining, lastKnownTimeRemaining, callTimer?.remainingTime, trialStatus?.minutesLeft]);

  useEffect(() => {
    const loadUserData = async () => {
      if (!isStorageInitialized || !firestoreStorage?.currentUser) return;
      
      try {
        setLoading(true);
        
        const adminStatus = firestoreStorage.isAdmin();
        setIsAdmin(adminStatus);
        
        const userData = await firestoreStorage.getUserData();
        const trial = await firestoreStorage.getTrialStatus();
        const timer = await firestoreStorage.getCallTimer();
        
        setUserInfo(userData);
        setTrialStatus(trial);
        setCallTimer(timer);
        
        console.log('User trial status:', trial);
        console.log('Call timer:', timer);
        
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();

    // Set up a periodic refresh to sync with Firebase data
    const refreshInterval = setInterval(async () => {
      if (isStorageInitialized && firestoreStorage?.currentUser) {
        try {
          const timer = await firestoreStorage.getCallTimer();
          setCallTimer(prevTimer => {
            // Only update if the data has actually changed
            if (prevTimer?.remainingTime !== timer?.remainingTime) {
              console.log('Updated call timer from Firebase:', timer);
              return timer;
            }
            return prevTimer;
          });
        } catch (error) {
          console.error('Failed to refresh call timer:', error);
        }
      }
    }, 2000); // Refresh every 2 seconds

    return () => clearInterval(refreshInterval);
  }, [isStorageInitialized, firestoreStorage]);

  // Reload user data after call ends to get updated storage values
  useEffect(() => {
    if (callEnded && firestoreStorage && isStorageInitialized) {
      const reloadAfterCall = async () => {
        try {
          console.log('Reloading user data after call ended...');
          const timer = await firestoreStorage.getCallTimer();
          setCallTimer(timer);
          console.log('Updated call timer after call:', timer);
        } catch (error) {
          console.error('Failed to reload user data after call:', error);
        }
      };
      
      // Add a small delay to ensure storage has been updated
      const timeoutId = setTimeout(reloadAfterCall, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [callEnded, firestoreStorage, isStorageInitialized]);

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return '0 seconds';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    
    if (minutes === 0) {
      return `${remainingSeconds} seconds`;
    } else if (remainingSeconds === 0) {
      return `${minutes} minutes`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  // Get the actual remaining time to display with improved logic
  const getDisplayRemainingTime = () => {
    // Priority order:
    // 1. Always prioritize live timeRemaining prop if available (most up-to-date)
    // 2. If call ended, use lastKnownTimeRemaining from the call
    // 3. Fall back to stored callTimer.remainingTime
    
    // Always use live timeRemaining if available - this is the most current value
    if (timeRemaining !== undefined && timeRemaining !== null && timeRemaining >= 0) {
      console.log('Using live timeRemaining:', timeRemaining);
      return timeRemaining;
    }
    
    // If call ended and we have lastKnownTimeRemaining, use that
    if (callEnded && lastKnownTimeRemaining !== undefined && lastKnownTimeRemaining !== null) {
      console.log('Using lastKnownTimeRemaining:', lastKnownTimeRemaining);
      return lastKnownTimeRemaining;
    }
    
    // Default to stored value from Firebase
    const stored = callTimer?.remainingTime || 0;
    console.log('Using stored callTimer.remainingTime:', stored);
    return stored;
  };

  const getTrialStatusDisplay = () => {
    if (isAdmin) {
      return {
        icon: <Crown size={16} />,
        label: 'Admin Access',
        sublabel: 'Unlimited usage',
        badgeText: 'ADMIN',
        badgeClass: 'admin'
      };
    }

    if (loading || !trialStatus || !callTimer) {
      return {
        icon: <Timer size={16} />,
        label: 'Loading...',
        sublabel: 'Checking trial status',
        badgeText: 'LOADING',
        badgeClass: 'loading'
      };
    }

    const actualRemainingSeconds = getDisplayRemainingTime();
    const actualRemainingMinutes = actualRemainingSeconds / 60;

    if (trialStatus.isExpired || actualRemainingSeconds <= 0) {
      return {
        icon: <AlertCircle size={16} />,
        label: 'Trial Expired',
        sublabel: 'Contact support to continue',
        badgeText: 'EXPIRED',
        badgeClass: 'expired'
      };
    }

    const daysLeft = trialStatus.daysLeft;
    
    if (daysLeft <= 3 || actualRemainingMinutes <= 5) {
      return {
        icon: <AlertCircle size={16} />,
        label: 'Trial Ending Soon',
        sublabel: `${daysLeft} days, ${formatTimeRemaining(actualRemainingSeconds)} left`,
        badgeText: 'URGENT',
        badgeClass: 'urgent'
      };
    }

    return {
      icon: <CheckCircle size={16} />,
      label: 'Trial Active',
      sublabel: `${daysLeft} days remaining`,
      badgeText: 'ACTIVE',
      badgeClass: 'active'
    };
  };

  const trialDisplay = getTrialStatusDisplay();

  // Determine the label for the time display
  const getTimeLabel = () => {
    if (isRecording) {
      return 'Time left:';
    } else if (callEnded && lastKnownTimeRemaining !== null) {
      return 'Time left:';
    } else if (timeRemaining !== undefined && timeRemaining !== null) {
      return 'Time left:';
    } else {
      return 'Talk time:';
    }
  };

  return (
    <div className="user-content">
      {isAdmin && (
        <div className="dropdown-item">
          <div className="item-text">
            <div className="item-title trial-title">
              <Crown size={16} />
              Admin Access
              <div className="beta-badge" style={{ transform: 'rotate(0deg)' }}>
                ADMIN
              </div>
            </div>
            <div className="item-description trial-description">
              Unlimited usage
            </div>
          </div>
        </div>
      )}
      
      {!isAdmin && !loading && trialStatus && callTimer && (
        <div className="dropdown-item">
          <div className="item-text">
            <div className="trial-details">
              <div className="trial-stat" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="trial-stat-label">Days Left:</span>
                  <span className="trial-stat-value">{trialStatus.daysLeft} days</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="trial-stat-label">
                    {getTimeLabel()}
                  </span>
                  <span className="trial-stat-value" style={{
                    color: getDisplayRemainingTime() <= 60 ? '#ff4444' : 'inherit',
                    fontWeight: (isRecording || (callEnded && lastKnownTimeRemaining !== null)) ? 'bold' : 'normal'
                  }}>
                    {formatTimeRemaining(getDisplayRemainingTime())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="dropdown-item">
        <div className="item-text">
          <div className="item-title">My Preptune ID:</div>
          <div className="item-description" style={{
            fontFamily: 'monospace',
            fontSize: '14px',
            wordBreak: 'break-all',
            padding: '8px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
            border: '1px solid rgba(255,255,255,0.2)',
            position: 'relative',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{
              filter: isIdVisible ? 'none' : 'blur(3px)',
              userSelect: isIdVisible ? 'text' : 'none',
              transition: 'filter 0.2s ease',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px',
              marginRight: '8px'
            }}>
              {persistentUserId || 'Loading...'}
            </span>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0
            }}>
              {isIdVisible && (
                <button
                  onClick={() => {
                    if (persistentUserId) {
                      navigator.clipboard.writeText(persistentUserId);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: copied ? '#22c55e' : 'currentColor',
                    cursor: 'pointer',
                    padding: '4px 6px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    opacity: 0.7,
                    transition: 'all 0.2s ease',
                    fontSize: '12px'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                  title={copied ? "Copied!" : "Copy ID"}
                >
                  {copied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                      </svg>
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9.50006C1 10.3285 1.67157 11.0001 2.5 11.0001H4L4 10.0001H2.5C2.22386 10.0001 2 9.7762 2 9.50006L2 2.50006C2 2.22392 2.22386 2.00006 2.5 2.00006L9.5 2.00006C9.77614 2.00006 10 2.22392 10 2.50006V4.00002H5.5C4.67158 4.00002 4 4.67159 4 5.50002V12.5C4 13.3284 4.67158 14 5.5 14H12.5C13.3284 14 14 13.3284 14 12.5V5.50002C14 4.67159 13.3284 4.00002 12.5 4.00002H11V2.50006C11 1.67163 10.3284 1.00006 9.5 1.00006H2.5C1.67157 1.00006 1 1.67163 1 2.50006V9.50006ZM5 5.50002C5 5.22388 5.22386 5.00002 5.5 5.00002H12.5C12.7761 5.00002 13 5.22388 13 5.50002V12.5C13 12.7762 12.7761 13 12.5 13H5.5C5.22386 13 5 12.7762 5 12.5V5.50002Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              )}
              
              <button
                onClick={() => setIsIdVisible(!isIdVisible)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1d1d1dff',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease',
                  fontSize: '12px'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                title={isIdVisible ? 'Hide ID' : 'Show ID'}
              >
                {isIdVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dropdown-item">
        <div className="item-text">
          <div className="item-description">
            <strong>Voice:</strong> {currentVoice?.name || 'Not selected'}
            <br />
          </div>
          <div className="summary-buttons">
            <button
              onClick={() => {
                changeVoice();
                setShowSummary(false);
              }}
              className="summary-btn primary"
              title="Change voice companion"
            >
              <span>
                <svg width="13" height="13" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.35355 1.85355C3.54882 1.65829 3.54882 1.34171 3.35355 1.14645C3.15829 0.951184 2.84171 0.951184 2.64645 1.14645L0.646447 3.14645C0.451184 3.34171 0.451184 3.65829 0.646447 3.85355L2.64645 5.85355C2.84171 6.04882 3.15829 6.04882 3.35355 5.85355C3.54882 5.65829 3.54882 5.34171 3.35355 5.14645L2.20711 4H9.5C11.433 4 13 5.567 13 7.5C13 7.77614 13.2239 8 13.5 8C13.7761 8 14 7.77614 14 7.5C14 5.01472 11.9853 3 9.5 3H2.20711L3.35355 1.85355ZM2 7.5C2 7.22386 1.77614 7 1.5 7C1.22386 7 1 7.22386 1 7.5C1 9.98528 3.01472 12 5.5 12H12.7929L11.6464 13.1464C11.4512 13.3417 11.4512 13.6583 11.6464 13.8536C11.8417 14.0488 12.1583 14.0488 12.3536 13.8536L14.3536 11.8536C14.5488 11.6583 14.5488 11.3417 14.3536 11.1464L12.3536 9.14645C12.1583 8.95118 11.8417 8.95118 11.6464 9.14645C11.4512 9.34171 11.4512 9.65829 11.6464 9.85355L12.7929 11H5.5C3.567 11 2 9.433 2 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
              </span>
              Change Voice
            </button>
          </div>
        </div>
      </div>

      <div className="dropdown-item">
        <div className="item-text">
          <div className="item-title">Clear Chat History</div>
          <div className="item-description">This button clears all chat history and starts fresh.</div>
          <div className="summary-buttons">
            <button
              onClick={handleStartOver}
              className="summary-btn primary"
              title="Clear all conversation history"
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.5 1C5.22386 1 5 1.22386 5 1.5C5 1.77614 5.22386 2 5.5 2H9.5C9.77614 2 10 1.77614 10 1.5C10 1.22386 9.77614 1 9.5 1H5.5ZM3 3.5C3 3.22386 3.22386 3 3.5 3H5H10H11.5C11.7761 3 12 3.22386 12 3.5C12 3.77614 11.7761 4 11.5 4H11V12C11 12.5523 10.5523 13 10 13H5C4.44772 13 4 12.5523 4 12V4L3.5 4C3.22386 4 3 3.77614 3 3.5ZM5 4H10V12H5V4Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
              </svg>
              Delete Conversation
            </button>
          </div>
        </div>
      </div>

      <div className="dropdown-item">
        <div className="summary-buttons">
          <button
            onClick={handleSignOut}
            className="summary-btn secondary"
            title="Sign out of your account"
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: '1px solid #dc2626'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 1C2.44771 1 2 1.44772 2 2V13C2 13.5523 2.44772 14 3 14H10.5C10.7761 14 11 13.7761 11 13.5C11 13.2239 10.7761 13 10.5 13H3V2L10.5 2C10.7761 2 11 1.77614 11 1.5C11 1.22386 10.7761 1 10.5 1H3ZM12.6036 4.89645C12.4083 4.70118 12.0917 4.70118 11.8964 4.89645C11.7012 5.09171 11.7012 5.40829 11.8964 5.60355L13.2929 7H6.5C6.22386 7 6 7.22386 6 7.5C6 7.77614 6.22386 8 6.5 8H13.2929L11.8964 9.39645C11.7012 9.59171 11.7012 9.90829 11.8964 10.1036C12.0917 10.2988 12.4083 10.2988 12.6036 10.1036L14.8536 7.85355C15.0488 7.65829 15.0488 7.34171 14.8536 7.14645L12.6036 4.89645Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInfoTabContent;